import { Tabs } from "shared/components/BaseTabs";
import type { Tab } from "shared/components/BaseTabs/types";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useAuthContext } from "providers/Auth";

type ScreeningTab = "screening-templates" | "discounts" | "landlord-surveys";

const SCREENING_TABS_CONFIG: {
  tabs: ScreeningTab[];
  displayMap: Record<ScreeningTab, string>;
  routes: Record<ScreeningTab, string>;
} = {
  tabs: ["screening-templates", "discounts", "landlord-surveys"],
  displayMap: {
    "screening-templates": "Screening templates",
    discounts: "Discount codes",
    "landlord-surveys": "Landlord surveys"
  },
  routes: {
    "screening-templates": "/verification-templates",
    discounts: "/discounts",
    "landlord-surveys": "/landlord-surveys"
  }
};

export const ScreeningTabs = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions, type } = useAuthContext();

  const handleTabChange = (tab: ScreeningTab) => {
    navigate(SCREENING_TABS_CONFIG.routes[tab]);
  };

  const isDiscounts = pathname.includes("/discounts");
  const isScreeningTemplates = pathname.includes("/verification-templates");
  const isLandlordSurveys = pathname.includes("/landlord-surveys");

  // Filter tabs based on permissions
  const availableTabs = useMemo(() => {
    const tabPermissions: Record<ScreeningTab, boolean> = {
      "screening-templates": permissions.canAccessScreeningTemplates !== false,
      discounts: permissions.canManageDiscounts !== false,
      "landlord-surveys": type.isBoomScreen
    };

    return SCREENING_TABS_CONFIG.tabs
      .filter((tab) => tabPermissions[tab])
      .map(
        (tab): Tab<ScreeningTab> => ({
          value: tab,
          label: SCREENING_TABS_CONFIG.displayMap[tab]
        })
      );
  }, [
    permissions.canAccessScreeningTemplates,
    permissions.canManageDiscounts,
    type.isBoomScreen
  ]);

  const selected = useMemo((): ScreeningTab => {
    if (isDiscounts) return "discounts";
    if (isLandlordSurveys) return "landlord-surveys";
    return "screening-templates";
  }, [isLandlordSurveys, isDiscounts, isScreeningTemplates]);

  return (
    <Tabs
      size="sm"
      tabStyle="line"
      fullWidthTabs={false}
      tabs={availableTabs}
      active={selected}
      onChange={handleTabChange}
    />
  );
};
